import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Complete, Logo, LogoWeb, Mic, SendMsg } from '../assests/ExportImageIcon'
import service from '../services/service';
import endpoints from '../services/endpoints';
import "./home.css"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL_AI;
const TENANT_ID = process.env.REACT_APP_TENANT_ID;

function createMessageId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function parseResponse(response) {
  const text = await response.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (!response.ok) throw new Error(data.message || `API returned HTTP ${response.status}`);
  return data;
}

/** Time to show the final assistant message before the completion screen (avg ~238 wpm + buffer). */
function autoCompleteDisplayDelayMs(text) {
  const AUTO_COMPLETE_READ_MIN_MS = 15000;
  const AUTO_COMPLETE_READ_MAX_MS = 60000;
  /** Average adult silent reading of plain English (~200–250 wpm; mean ~238). */
  const READING_WPM = 238;
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  const readingMs = Math.round((words / READING_WPM) * 60 * 1000);
  const withBuffer = readingMs + 5000;
  return Math.min(AUTO_COMPLETE_READ_MAX_MS, Math.max(AUTO_COMPLETE_READ_MIN_MS, withBuffer));
}

const Home = () => {
  const { token } = useParams();
  const [conversationId, setConversationId] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceInputSupported, setVoiceInputSupported] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  const conversationIdRef = useRef(null);
  const isBusyRef = useRef(false);
  const isEndedRef = useRef(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const latestTranscriptRef = useRef("");
  const manualStopRef = useRef(false);
  const handleSendRef = useRef(null);
  const messageListRef = useRef(null);
  const messagesRef = useRef([]);
  const isEndingRef = useRef(false);
  const isDataSavedRef = useRef(false);
  const conversationInitRef = useRef(false);
  const textareaRef = useRef(null);

  const showLogoSample = messages.length === 0 && !isEnded;
  const showChatSection = messages.length > 0 && !isEnded;
  const showComplete = isEnded;
  /** Profile fetch or first conversation create — hide empty logo and block input until chat is ready */
  const isPreChatLoading = !isEnded && messages.length === 0 && (userDetails === null || isBusy);
  const canEndChat = conversationId && !isEnded && !isBusy && !isDataSaved;
  const isInputDisabled = isEnded || isPreChatLoading || isDataSaved;
  const canSendMessage =
    !isListening && !isEnded && !isBusy && !isPreChatLoading && !isDataSaved && Boolean(inputValue.trim());

  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { isBusyRef.current = isBusy; }, [isBusy]);
  useEffect(() => { isEndedRef.current = isEnded; }, [isEnded]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { isDataSavedRef.current = isDataSaved; }, [isDataSaved]);
  useEffect(() => {
    if (!isDataSaved) return;
    const recognition = recognitionRef.current;
    if (!recognition || !isListening) return;
    manualStopRef.current = true;
    try { recognition.stop(); } catch {}
    setIsListening(false);
  }, [isDataSaved, isListening]);
  useEffect(() => {
    if (!token) return;
    const ac = new AbortController();
    (async () => {
      try {
        const res = await service.get(
          endpoints.user.chatUser.replace("{token}", token),
          {},
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        setUserDetails(res?.data?.data || {});
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || ac.signal.aborted) return;
        setUserDetails({});
      }
    })();
    return () => ac.abort();
  }, [token]);

  useEffect(() => {
    messageListRef.current?.scrollTo({ top: messageListRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    setVoiceInputSupported(true);
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.addEventListener("start", () => {
      setIsListening(true);
    });

    recognition.addEventListener("result", (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript.trim();
        if (event.results[i].isFinal) {
          finalTranscriptRef.current = `${finalTranscriptRef.current} ${t}`.trim();
        } else {
          interim = `${interim} ${t}`.trim();
        }
      }
      latestTranscriptRef.current = (finalTranscriptRef.current || interim).trim();
      setInputValue(latestTranscriptRef.current);
    });

    recognition.addEventListener("end", () => {
      if (manualStopRef.current || isBusyRef.current || isEndedRef.current || isDataSavedRef.current) {
        manualStopRef.current = false;
        setIsListening(false);
        return;
      }
      try {
        recognition.start();
      } catch {
        setIsListening(false);
      }
    });

    recognition.addEventListener("error", (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        manualStopRef.current = true;
        setIsListening(false);
      }
    });

    return () => {
      manualStopRef.current = true;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  function addMessage(role, text) {
    setMessages(prev => [...prev, { id: createMessageId(), role, text }]);
  }

  async function startConversationAndShowWelcome(signal) {
    if (conversationIdRef.current) return conversationIdRef.current;
    setIsBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/conversations`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT_ID, userId: token, userDetails: userDetails, channel: "web", locale: "en_US" }),
        ...(signal ? { signal } : {}),
      });
      const data = await parseResponse(res);
      const cid = data.conversationId;
      setConversationId(cid);
      conversationIdRef.current = cid;
      addMessage("assistant", data.firstMessage);
      return cid;
    } catch (err) {
      console.error("Failed to start conversation:", err);
      throw err;
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (userDetails === null || !token || isEnded) return;
    if (conversationIdRef.current || conversationInitRef.current) return;
    conversationInitRef.current = true;
    const ac = new AbortController();
    (async () => {
      try {
        await startConversationAndShowWelcome(ac.signal);
      } catch (e) {
        if (e?.name === "AbortError") return;
        conversationInitRef.current = false;
      }
    })();
    return () => {
      ac.abort();
      conversationInitRef.current = false;
    };
  }, [userDetails, token, isEnded]);

  async function handleSend(text) {
    const msg = (text || inputValue).trim();
    if (!msg || isBusyRef.current || isEndedRef.current || isDataSavedRef.current) return;

    setInputValue("");

    let cid = conversationIdRef.current;

    if (!cid) {
      try {
        cid = await startConversationAndShowWelcome();
      } catch {
        return;
      }
    }

    addMessage("user", msg);
    setIsBusy(true);
    let autoComplete = false;
    let closingMessage = "";
    try {
      const res = await fetch(`${API_BASE_URL}/v1/conversations/${cid}/messages`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messageId: createMessageId(), text: msg }),
      });
      const data = await parseResponse(res);
      closingMessage = data.text || "";
      addMessage("assistant", closingMessage);
      autoComplete = data?.nextAction === "complete";
    } catch {
      addMessage("assistant", "Something went wrong. Please try again.");
    } finally {
      setIsBusy(false);
    }
    if (autoComplete) {
      endConversation(autoCompleteDisplayDelayMs(closingMessage));
    }
  }

  handleSendRef.current = handleSend;

  async function endConversation(completionDelayMs = null) {
    if (!conversationIdRef.current || isEndedRef.current || isEndingRef.current) return;
    isEndingRef.current = true;
    setIsBusy(true);
    try {
      const summaryRes = await fetch(`${API_BASE_URL}/v1/conversations/${conversationIdRef.current}/summary`);
      const summaryData = await parseResponse(summaryRes);
      await service.put(
        endpoints.user.chatUser.replace("{token}", token),
        {
          chats: messagesRef.current,
          summary: summaryData || {},
          ...(summaryData?.leadProfile || {}),
        }
      );
      setIsDataSaved(true);
      isDataSavedRef.current = true;
      const showComplete = () => {
        setIsEnded(true);
        isEndedRef.current = true;
      };
      if (completionDelayMs > 0) {
        setTimeout(showComplete, completionDelayMs);
      } else {
        showComplete();
      }
    } catch (err) {
      console.error("Failed to end conversation:", err);
      isEndingRef.current = false;
    } finally {
      setIsBusy(false);
    }
  }

  function toggleVoiceInput() {
    const recognition = recognitionRef.current;
    if (!recognition || isEndedRef.current || isBusyRef.current || isDataSavedRef.current) return;
    if (isListening) {
      manualStopRef.current = true;
      recognition.stop();
      return;
    }
    manualStopRef.current = false;
    finalTranscriptRef.current = "";
    latestTranscriptRef.current = "";
    setInputValue("");
    textareaRef.current.style.height = '72px';
    try { recognition.start(); } catch {}
  }

  function stopVoiceInput() {
    const recognition = recognitionRef.current;
    if (!recognition || !isListening) return;
    manualStopRef.current = true;
    try { recognition.stop(); } catch {}
  }

  return (
    <div>
      <div className='aiBox'>
        <div className='topHeader'>
            <img src={Logo} />
            <div
              className={`endChat ${!canEndChat ? 'disabled' : ''}`}
              onClick={() => canEndChat && endConversation()}
            >
              END CHAT & SUMMARY
            </div>
        </div>
        <div className='chatArea' aria-busy={isPreChatLoading}>

            {showLogoSample && (
              <div className='logoSample'>
                {isPreChatLoading ? (
                  <div className='chatBootstrap' role='status' aria-live='polite'>
                    <div className='chatBootstrapSpinner' aria-hidden />
                    <p className='chatBootstrapTitle'>
                      {userDetails === null ? 'Getting things ready' : 'Starting your chat'}
                    </p>
                    <p className='chatBootstrapHint'>
                      {userDetails === null
                        ? 'Loading your profile so we can personalize this session.'
                        : 'Connecting you with your coach — you can type in a moment.'}
                    </p>
                  </div>
                ) : (
                  <img src={LogoWeb} alt='' />
                )}
              </div>
            )}

            {showChatSection && (
              <div className='chatSection' ref={messageListRef}>
                {messages.map(msg => (
                  <div key={msg.id} className={`${msg.role === 'assistant' ? 'leftChat' : 'rightChat'} cmnChatS`}>
                    {msg.text}
                  </div>
                ))}
                {isBusy && <div className='leftChat cmnChatS'>...</div>}
              </div>
            )}

            {showComplete && (
              <div className='complete'>
                  <img src={Complete} />
                  <p>Completed Successfully</p>
              </div>
            )}

            <div className='sendMessage'>
                <div className={`inputPart ${isListening ? 'listening' : ''}`}>
                    <textarea
                      ref={textareaRef}
                      placeholder={
                        isPreChatLoading
                          ? userDetails === null
                            ? 'Preparing…'
                            : 'Starting chat…'
                          : 'Type here...'
                      }
                      value={inputValue}
                      rows={1}
                      onChange={(e) => {
                        setInputValue(e.target.value);

                        // auto resize
                        e.target.style.height = 'auto';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && canSendMessage) {
                          e.preventDefault();
                          handleSend();

                          // reset height after send
                          e.currentTarget.style.height = 'auto';
                        }
                      }}
                      disabled={isInputDisabled}
                      className="resize-none overflow-hidden w-full"
                    />
                    {isListening ? (
                      <div className='voiceActions'>
                        <div className='voiceVisualizer' aria-hidden='true'>
                          {Array.from({ length: 10 }).map((_, index) => (
                            <span
                              key={index}
                              className='voiceBar'
                              style={{ animationDelay: `${index * 0.08}s` }}
                            />
                          ))}
                        </div>
                        <button type='button' className='voiceBtn stop' onClick={stopVoiceInput}>
                          Stop
                        </button>
                      </div>
                    ) : (
                      <img
                        src={Mic}
                        onClick={voiceInputSupported && !isInputDisabled ? toggleVoiceInput : undefined}
                        style={{
                          cursor: voiceInputSupported && !isBusy && !isInputDisabled ? 'pointer' : 'default',
                          opacity: isListening || isInputDisabled ? 0.45 : 1,
                        }}
                      />
                    )}
                </div>
                <div
                  className={`sendM ${canSendMessage ? 'active' : ''}`}
                  onClick={() => canSendMessage && handleSend()}
                  style={{ cursor: canSendMessage ? 'pointer' : 'not-allowed' }}
                >
                  <img src={SendMsg} />
                </div>
            </div>
        </div>
      </div>
    </div>
  )
}

export default Home
